import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ACTION_LABEL = {
  LOGIN: "Login",
  LOGOUT: "Logout",
  CREATE: "Buat",
  UPDATE: "Ubah",
  DELETE: "Hapus",
  IMPORT: "Import",
  EXPORT: "Export",
};

const ACTIONS = ["LOGIN", "LOGOUT", "CREATE", "UPDATE", "DELETE", "IMPORT", "EXPORT"];

function formatDay(day) {
  if (!day) return "-";
  const d = new Date(`${day}T00:00:00`);
  return d.toLocaleDateString("id-ID", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
}

export default function Audit() {
  const [data, setData] = useState(null);
  const [action, setAction] = useState("ALL");
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (params) => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/audit", { params });
      setData(res);
      setDate(res.date || "");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({});
  }, []);

  useEffect(() => {
    const params = {};
    if (action !== "ALL") params.action = action;
    load(params);
    // eslint-disable-next-line
  }, [action]);

  const goPrev = () => {
    if (!data?.prev_date) return;
    const params = { date: data.prev_date };
    if (action !== "ALL") params.action = action;
    load(params);
  };

  const goNext = () => {
    if (!data?.next_date) return;
    const params = { date: data.next_date };
    if (action !== "ALL") params.action = action;
    load(params);
  };

  if (!data) return <div className="p-6 md:p-10 label-overline">Memuat...</div>;

  const counts = data.counts || {};

  return (
    <div className="p-6 md:p-10">
      <div className="mb-6 md:mb-10">
        <div className="label-overline">Riwayat</div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mt-2">
          Audit Trail
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div className="border border-neutral-200 p-5">
          <div className="label-overline">Total Aksi</div>
          <div className="font-display font-black text-4xl mt-2 tracking-tighter" data-testid="audit-total">{data.total}</div>
        </div>
        {Object.entries(counts).slice(0, 3).map(([act, count]) => (
          <div key={act} className="border border-neutral-200 p-5">
            <div className="label-overline">{ACTION_LABEL[act] || act}</div>
            <div className="font-display font-black text-4xl mt-2 tracking-tighter">{count}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex flex-col md:flex-row md:items-end gap-3 mb-4">
        <div className="md:w-64">
          <Label className="label-overline">Filter Aksi</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="audit-action-filter" className="mt-1 rounded-none border-black h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="ALL">Semua Aksi</SelectItem>
              {ACTIONS.map((a) => (
                <SelectItem key={a} value={a}>
                  {ACTION_LABEL[a]} {counts[a] ? `(${counts[a]})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Day navigator */}
      <div className="border border-black flex items-center justify-between p-3 mb-0 bg-black text-white">
        <button
          data-testid="audit-prev-day"
          onClick={goPrev}
          disabled={!data.prev_date}
          className="p-2 border border-white disabled:border-neutral-600 disabled:text-neutral-500 hover:bg-white hover:text-black transition-colors duration-150 disabled:hover:bg-transparent disabled:hover:text-neutral-500 flex items-center gap-1"
          aria-label="Hari sebelumnya"
        >
          <ChevronLeft size={16} /> <span className="text-xs uppercase tracking-wider hidden sm:inline">Lebih Lama</span>
        </button>
        <div className="text-center min-w-0 flex-1 px-2">
          <div className="label-overline text-neutral-400">Menampilkan Hari</div>
          <div className="font-display font-bold text-sm sm:text-base tracking-tight truncate" data-testid="audit-current-day">
            {data.date ? formatDay(data.date) : "Belum ada data"}
          </div>
          <div className="text-xs text-neutral-400 mt-0.5" data-testid="audit-day-count">
            {data.day_total} aksi
          </div>
        </div>
        <button
          data-testid="audit-next-day"
          onClick={goNext}
          disabled={!data.next_date}
          className="p-2 border border-white disabled:border-neutral-600 disabled:text-neutral-500 hover:bg-white hover:text-black transition-colors duration-150 disabled:hover:bg-transparent disabled:hover:text-neutral-500 flex items-center gap-1"
          aria-label="Hari berikutnya"
        >
          <span className="text-xs uppercase tracking-wider hidden sm:inline">Lebih Baru</span> <ChevronRight size={16} />
        </button>
      </div>

      <div className="border border-t-0 border-neutral-200" data-testid="audit-list">
        <div className="hidden md:grid grid-cols-[120px_120px_140px_1fr_140px] border-b border-neutral-200">
          <div className="label-overline p-3">Aksi</div>
          <div className="label-overline p-3">Entity</div>
          <div className="label-overline p-3">User</div>
          <div className="label-overline p-3">Detail</div>
          <div className="label-overline p-3">Waktu</div>
        </div>
        {loading ? (
          <div className="p-8 text-center label-overline">Memuat...</div>
        ) : data.logs.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 text-sm">Tidak ada aktivitas pada hari ini.</div>
        ) : (
          data.logs.map((log) => (
            <div
              key={log.id}
              className="md:grid md:grid-cols-[120px_120px_140px_1fr_140px] border-b border-neutral-100 p-3 md:p-0 text-sm"
              data-testid={`audit-${log.id}`}
            >
              <div className="md:p-3">
                <span className="inline-block px-2 py-0.5 text-[0.65rem] uppercase tracking-wider border border-black font-semibold">
                  {ACTION_LABEL[log.action] || log.action}
                </span>
              </div>
              <div className="md:p-3 text-neutral-600 mt-1 md:mt-0">{log.entity}</div>
              <div className="md:p-3 text-neutral-600 truncate mt-1 md:mt-0">{log.user_email}</div>
              <div className="md:p-3 text-neutral-700 mt-1 md:mt-0">{log.detail || "-"}</div>
              <div className="md:p-3 text-neutral-500 text-xs mt-1 md:mt-0">
                {new Date(log.timestamp).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </div>
            </div>
          ))
        )}
      </div>

      {data.available_days.length > 0 && (
        <div className="mt-3 label-overline text-neutral-500" data-testid="audit-days-info">
          Total {data.available_days.length} hari dengan aktivitas
        </div>
      )}
    </div>
  );
}
